import { EnumProfiloSelezionatoTipo } from '@framework/api-client';
import type {
  DatiSessioneUtente,
  UtenteProfiloSelezionato,
} from '@framework/api-client';
import { createListenerMiddleware, createSlice } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';
import { useEffect } from 'react';
import { shallowEqual, useSelector } from 'react-redux';
import { getAuthGlobalOptions } from '../options';
import { PermissionLogic } from '../permissions/PermissionLogic';
import type { PermissionType } from '../permissions/PermissionType';
import { AuthStorage } from '../storage/AuthStorage';
import { AuthSubject } from './AuthSubject';

export interface AuthState {
  isLogged: boolean;
  isPrelogged: boolean;
  strategy: string | null;
  accessToken: string | null;
  refreshToken: string | null;
  officeId?: string | null;
  roleId?: string | null;
  legalEntityId?: string | null;
  isAccessTokenExpired?: boolean;
  logoutUrls: string[] | null;
  isRefreshTokenExpired: boolean;
  current: DatiSessioneUtente | null;
  /** Dati esterni dell'utente, recuperati da servizi terzi (gestionale, documentale, DB del personale) */
  userData?: Record<string, any> | null;
  fetchCount: number;
  debugMode?: boolean;
  simulateExpiredRefreshToken?: boolean;
  /**
   * L'utente loggato ha chiesto di ri-scegliere il profilo ("Cambia profilo").
   * Forza `isLogged=false` così da tornare alla schermata di scelta,
   * indipendentemente da `profiloSelezionato`. Persistito in `AuthStorage` per
   * sopravvivere al refresh della pagina (l'intento dell'utente non va perso).
   */
  reselectionRequested?: boolean;
}

/**
 * Interfaccia parziale per ottenere lo stato dell'autenticazione dal Root.
 */
export interface IRootAuthState {
  auth: AuthState;
}

/**
 * Sottoinsieme dei campi di autenticazione, derivato da
 * {@link AuthState}. Usato come snapshot dello stato auth (es. payload
 * dell'evento di logout), così da restare allineato con `AuthState` senza
 * ricopiarne i campi a mano.
 */
export type AuthStateSubset = Pick<
  AuthState,
  | 'isLogged'
  | 'isPrelogged'
  | 'strategy'
  | 'refreshToken'
  | 'accessToken'
  | 'fetchCount'
  | 'logoutUrls'
>;

function resolveLegalEntityId(
  profile: UtenteProfiloSelezionato | undefined,
  fallback: string | null,
): string | null {
  switch (profile?.tipo) {
    case EnumProfiloSelezionatoTipo.PG:
      return profile.id ?? null;
    case EnumProfiloSelezionatoTipo.PF:
      return null;
    default:
      return fallback;
  }
}

export const AuthSlice = createSlice({
  name: 'auth',
  initialState: () =>
    ({
      isRefreshTokenExpired: false,
      current: null,
      accessToken: null,
      fetchCount: 0,
      isAccessTokenExpired: false,
      /**
       * Carichiamo i dati iniziali dal `sessionStorage` in caso siano
       * presenti.
       */
      ...AuthStorage.initialState(),
    } as AuthState),
  reducers: {
    /**
     * Al login, salviamo il token.
     */
    loginSucceed: (
      state,
      action: PayloadAction<{
        strategy: string;
        accessToken: string;
        refreshToken: string;
        logoutUrls?: string[] | null;
      }>,
    ) => {
      const { strategy, accessToken, refreshToken, logoutUrls } =
        action.payload;
      const update = {
        strategy,
        refreshToken,
        isLogged: false,
        isPrelogged: true,
        userData: state.userData,
        legalEntityId: null,
        reselectionRequested: false,
        logoutUrls: process.env.MOCK_LOGOUT_URL
          ? [process.env.MOCK_LOGOUT_URL]
          : logoutUrls ?? null,
      };
      AuthStorage.saveState(update);
      return {
        ...state,
        ...update,
        accessToken,
        isAccessTokenExpired: false,
        isRefreshTokenExpired: false,
        fetchCount: state.fetchCount + 1,
        current: null,
      };
    },
    /**
     * Rimozione del token dalla sessione.
     */
    logout: (
      state,
      action: PayloadAction<{ isRefreshTokenExpired?: boolean } | undefined>,
    ) => {
      const update = {
        strategy: null,
        isPrelogged: false,
        isLogged: false,
        logoutUrls: null,
        refreshToken: null,
        officeId: null,
        roleId: null,
        legalEntityId: null,
        reselectionRequested: false,
      };
      AuthStorage.saveState(update);
      return {
        ...state,
        ...update,
        accessToken: null,
        isRefreshTokenExpired: action.payload?.isRefreshTokenExpired ?? false,
        current: null,
      };
    },
    /**
     * Utente corrente caricato. Quando questa richiesta ha esito positivo,
     * vuol dire che l'utente è autorizzato quindi possiamo impostare il token.
     */
    currentLoaded: (
      state,
      action: PayloadAction<{ current: DatiSessioneUtente }>,
    ) => {
      const { requiresRoleSelection, requiresLegalEntitySelection } =
        getAuthGlobalOptions();
      const { roleId, officeId } = PermissionLogic.getSelectedOfficeAndRole(
        action.payload.current,
      );

      const loggedUser = action.payload.current.utenteLoggato;
      const selectedProfile = loggedUser?.profiloSelezionato;

      const legalEntityId = resolveLegalEntityId(
        selectedProfile,
        state.legalEntityId ?? null,
      );

      const roleResolved = requiresRoleSelection
        ? officeId != null && roleId != null
        : true;
      // Stessa logica del gating dei ruoli (BO): se la selezione è richiesta,
      // l'utente resta `isPrelogged` finché non ha scelto "per conto di". Il
      // backend valorizza `profiloSelezionato` (PF di default per chi non ha
      // PG). La selezione è considerata risolta quando: PF (legal entity
      // legittimamente nulla) oppure PG con un `id` valorizzato. Una PG senza
      // `id` non è una selezione completa, quindi resta `isPrelogged`.
      // Se l'utente non è operatore di alcuna PG (es. login PA / dipendente
      // senza ditte associate) non c'è nulla da scegliere: la selezione è
      // risolta a prescindere e la schermata "per conto di" non va mostrata.
      const hasSelectableLegalEntities =
        (loggedUser?.personeGiuridicheOperatore?.length ?? 0) > 0;
      const legalEntityResolved = requiresLegalEntitySelection
        ? !hasSelectableLegalEntities ||
          selectedProfile?.tipo === EnumProfiloSelezionatoTipo.PF ||
          (selectedProfile?.tipo === EnumProfiloSelezionatoTipo.PG &&
            selectedProfile.id != null)
        : true;

      const update = {
        refreshToken: state.refreshToken,
        strategy: state.strategy,
        isLogged:
          roleResolved && legalEntityResolved && !state.reselectionRequested,
        isPrelogged: true,
        roleId: requiresRoleSelection ? roleId : null,
        officeId: requiresRoleSelection ? officeId : null,
        legalEntityId,
        userData: state.userData,
        logoutUrls: state.logoutUrls,
        reselectionRequested: state.reselectionRequested ?? false,
      };
      AuthStorage.saveState(update);
      return {
        ...state,
        ...update,
        current: action.payload.current,
      };
    },
    /**
     * Token ricaricato.
     */
    tokenRefreshed: (
      state,
      action: PayloadAction<{
        accessToken: string;
        refreshToken: string;
        legalEntityId?: string | null;
      }>,
    ) => {
      const { accessToken, refreshToken } = action.payload;
      const legalEntityId =
        action.payload.legalEntityId === undefined
          ? state.legalEntityId ?? null
          : action.payload.legalEntityId;
      const update = {
        strategy: state.strategy,
        refreshToken,
        legalEntityId,
        isLogged: state.isLogged,
        isPrelogged: true,
        userData: state.userData,
        logoutUrls: state.logoutUrls,
        reselectionRequested: state.reselectionRequested ?? false,
      };
      AuthStorage.saveState(update);
      return {
        ...state,
        ...update,
        isAccessTokenExpired: false,
        accessToken,
      };
    },
    /**
     * Forza il ricaricamento dell'utente corrente (e quindi il ricalcolo di
     * `isLogged`) senza cambiare token: utile dopo uno switch di profilo in cui
     * `isLogged` non transita e l'effect di fetch non riparte da solo.
     */
    reloadCurrent: state => ({
      ...state,
      fetchCount: state.fetchCount + 1,
    }),
    /**
     * L'utente loggato chiede di ri-scegliere il profilo ("Cambia profilo").
     * Forza lo stato pre-loggato (`isLogged=false`) per tornare alla schermata
     * di scelta; il flag viene azzerato quando l'utente seleziona un profilo.
     */
    requestReselection: state => {
      const update = {
        isLogged: false,
        legalEntityId: null,
        reselectionRequested: true,
      };
      AuthStorage.saveState({
        strategy: state.strategy,
        refreshToken: state.refreshToken,
        isPrelogged: state.isPrelogged ?? true,
        logoutUrls: state.logoutUrls,
        officeId: state.officeId,
        roleId: state.roleId,
        ...update,
      });
      return { ...state, ...update };
    },
    clearReselection: state => {
      const update = { reselectionRequested: false };
      AuthStorage.saveState({
        strategy: state.strategy,
        refreshToken: state.refreshToken,
        isLogged: state.isLogged,
        isPrelogged: state.isPrelogged ?? true,
        legalEntityId: state.legalEntityId ?? null,
        logoutUrls: state.logoutUrls,
        officeId: state.officeId,
        roleId: state.roleId,
        ...update,
      });
      return { ...state, ...update };
    },
    /**
     * Access token scaduto, per richieste in background (polling)
     */
    accessTokenExpired: state => {
      return {
        ...state,
        isAccessTokenExpired: true,
      };
    },
    simulateExpiredRefreshToken: (
      state,
      { payload: flag = true }: PayloadAction<boolean | undefined>,
    ) => ({
      ...state,
      simulateExpiredRefreshToken: flag,
    }),
    userDataLoaded: (state, action: PayloadAction<Record<string, any>>) => ({
      ...state,
      userData: { ...(state.userData ?? {}), ...action.payload },
    }),
  },
});

export const AuthActions = AuthSlice.actions;

/**
 * Hook per sapere se si è loggati.
 */
export const useAuth = () =>
  useSelector(
    (state: IRootAuthState) => ({
      isLogged: state.auth.isLogged,
      isPrelogged: state.auth.isPrelogged,
      strategy: state.auth.strategy,
      refreshToken: state.auth.refreshToken,
      accessToken: state.auth.accessToken,
      fetchCount: state.auth.fetchCount,
      logoutUrls: state.auth.logoutUrls,
      reselectionRequested: state.auth.reselectionRequested ?? false,
    }),
    shallowEqual,
  );

export type UseAuthResult = ReturnType<typeof useAuth>;

export const useCurrentUser = () =>
  useSelector((state: IRootAuthState) => state.auth.current, shallowEqual);

/**
 * Hook per controllare i permessi
 */
export const usePermissions = () => {
  const user = useCurrentUser();
  return {
    has(permission: PermissionType | PermissionType[]) {
      return PermissionLogic.has(user, permission);
    },
  };
};

export const useOnRefreshTokenExpired = (onExpired: () => void) => {
  const isRefreshTokenExpired = useSelector(
    (state: IRootAuthState) => state.auth.isRefreshTokenExpired,
    shallowEqual,
  );
  useEffect(() => {
    if (isRefreshTokenExpired) {
      onExpired();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRefreshTokenExpired]);
};

/**
 * @deprecated Utilizziamo direttamente `useCurrentUser`: i dati custom
 * sono contenuti in `datiCustomAggiuntivi`.
 */
export const useCurrentUserData = () =>
  useSelector(
    (state: IRootAuthState) => state.auth.userData ?? {},
    shallowEqual,
  );

export const logoutListenerMiddleware =
  createListenerMiddleware<IRootAuthState>();
logoutListenerMiddleware.startListening({
  actionCreator: AuthSlice.actions.logout,
  effect: (action, listnerApi) => {
    const { auth } = listnerApi.getOriginalState();
    AuthSubject.next({
      type: 'logout',
      auth: {
        isLogged: auth.isLogged,
        isPrelogged: auth.isPrelogged,
        strategy: auth.strategy,
        refreshToken: auth.refreshToken,
        accessToken: auth.accessToken,
        fetchCount: auth.fetchCount,
        logoutUrls: auth.logoutUrls,
      },
    });
  },
});
